export default class Video {
	constructor() {
		this.videoContainers = document.querySelectorAll('[data-video-container]')

		if (!this.videoContainers.length) return

		this.abortController = new AbortController()
		this.rafs = []

		this.#init()
	}

	#init() {
		this.videoContainers.forEach(container => this.#setupVideoControls(container))
	}

	#setupVideoControls(container) {
		const video = container.querySelector('video')

		if (!video) return

		const playButton = container.querySelector('[data-play-button]')
		const muteButton = container.querySelector('[data-mute-button]')

		const progressBar = container.querySelector('[data-progress-bar]')
		const currentTime = container.querySelector('[data-current-time]')
		const endTime = container.querySelector('[data-end-time]')

		if (video.autoplay) this.#forceAutoplay(video)

		this.#setupPlayButton(video, playButton)
		this.#setupMuteButton(video, muteButton)
		this.#setupProgressBar(video, progressBar, currentTime, endTime)
	}

	// Browsers only honour autoplay when the video is muted *as a property*.
	// The HTML `muted` attribute isn't always enough (framework re-renders or
	// the autoplay policy can drop it), so we set it explicitly and call play(),
	// swallowing the rejected promise the policy throws when it still blocks us.
	#forceAutoplay(video) {
		const { signal } = this.abortController

		video.muted = true
		video.defaultMuted = true
		video.playsInline = true

		const tryPlay = () => {
			const playPromise = video.play()
			if (playPromise !== undefined) playPromise.catch(() => {})
		}

		if (video.readyState >= 2) {
			tryPlay()
		} else {
			video.addEventListener('loadeddata', tryPlay, { once: true, signal })
		}

		// Retry once the video scrolls into view — covers cases where the policy
		// only allows playback for elements visible in the viewport.
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting && video.paused) tryPlay()
				})
			},
			{ threshold: 0.1 }
		)
		observer.observe(video)
		signal.addEventListener('abort', () => observer.disconnect())
	}

	#setupPlayButton(video, playButton) {
		if (!playButton) return
		const { signal } = this.abortController

		const playIcon = playButton.querySelector('.play-icon')
		const pauseIcon = playButton.querySelector('.pause-icon')

		this.#toggleButton(playIcon, pauseIcon, !video.paused)

		video.addEventListener('play', () => this.#toggleButton(playIcon, pauseIcon, true), { signal })
		video.addEventListener('pause', () => this.#toggleButton(playIcon, pauseIcon, false), { signal })
		playButton.addEventListener('click', () => (video.paused ? video.play() : video.pause()), { signal })
	}

	#setupMuteButton(video, muteButton) {
		if (!muteButton) return
		const { signal } = this.abortController

		const muteIcon = muteButton.querySelector('.mute-icon')
		const volumeIcon = muteButton.querySelector('.volume-icon')

		this.#toggleButton(muteIcon, volumeIcon, !video.muted)

		video.addEventListener('volumechange', () => this.#toggleButton(muteIcon, volumeIcon, !video.muted), { signal })
		muteButton.addEventListener('click', () => (video.muted = !video.muted), { signal })
	}

	#setupProgressBar(video, progressBar, currentTime, endTime) {
		if (!progressBar) return
		const { signal } = this.abortController

		let raf = null

		const update = () => {
			const progress = (video.currentTime / video.duration) * 100
			progressBar.style.width = `${progress}%`
			if (currentTime) currentTime.textContent = this.#formatTime(video.currentTime)
			if (!video.paused) {
				raf = requestAnimationFrame(update)
				this.rafs.push(raf)
			}
		}

		video.addEventListener(
			'play',
			() => {
				raf = requestAnimationFrame(update)
				this.rafs.push(raf)
			},
			{ signal }
		)

		video.addEventListener(
			'pause',
			() => {
				cancelAnimationFrame(raf)
				update()
			},
			{ signal }
		)

		video.addEventListener('seeked', () => update(), { signal })

		video.addEventListener(
			'loadedmetadata',
			() => {
				if (endTime) endTime.textContent = this.#formatTime(video.duration)
			},
			{ signal }
		)
	}

	#formatTime(seconds) {
		const mins = Math.floor(seconds / 60)
		const secs = Math.floor(seconds % 60)
		return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
	}

	#toggleButton(activeIcon, inactiveIcon, isActive) {
		activeIcon.classList.toggle('is-hidden', isActive)
		inactiveIcon.classList.toggle('is-hidden', !isActive)
	}

	destroy() {
		this.abortController?.abort()
		this.rafs?.forEach(raf => cancelAnimationFrame(raf))
		this.rafs = []
	}
}
